import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { SnackbarContext } from 'components';
import { DateFormat } from 'config';
import { format, isValid, parseISO } from 'date-fns';
import { CandidateVacancyResult } from 'lib/Model/SimilarCandidates';
import { toMonetary } from 'lib/Utility';
import * as React from 'react';
import {
  Feedback,
  FeedbackControls,
} from 'views/SimilarCandidates/components/FeedbackControls';
import { referralSteps } from 'views/VacancyMatcher/MatcherGuide';

// the candidate list's reasons, minus the ones about knowing the candidate
const badReasons = [
  'Different occupation',
  'Wrong career path',
  'Experience not recent',
  'Wrong skills',
  'Wrong sector',
  'Wrong scope/seniority',
  'Over/underqualified',
  'Fails a must-have',
  'Salary mismatch',
  'Already put forward',
];

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(1),
    alignItems: 'start',
    alignContent: 'start',
  },
  badge: {
    width: 30,
    height: 30,
    borderRadius: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(44, 6, 3, 0.06)',
    color: theme.palette.primary.main,
  },
  rank: {
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
  },
  body: {
    minWidth: 0,
  },
  topLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 700,
  },
  linkButton: {
    padding: 2,
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
  },
  linkIcon: {
    fontSize: 15,
  },
  // keeps the copy icon next to its reference, same look as the open link
  reference: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
  },
  meta: {
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
  },
  chip: {
    height: 22,
    fontSize: 11,
    fontWeight: 600,
  },
  payBelowChip: {
    color: theme.palette.warning.dark,
    borderColor: theme.palette.warning.main,
  },
  matchReason: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(1.25),
    borderLeft: `3px solid ${theme.palette.secondary.main}`,
    fontStyle: 'italic',
  },
  scoreCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 2,
  },
  scoreValue: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.primary.main,
  },
  scoreLabel: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  expandBtn: {
    minWidth: 0,
    padding: theme.spacing(0.25, 0),
    color: theme.palette.primary.main,
    fontSize: 11,
    fontWeight: 700,
  },
  expandIcon: {
    transition: 'transform 200ms ease',
    fontSize: 18,
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  wide: {
    gridColumn: '2 / span 2',
  },
  detailsBox: {
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: '#FAFAFA',
    borderRadius: 4,
    border: '1px solid #ECECEC',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  subScores: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  subScoreLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
  },
  subScoreValue: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const parsed = parseISO(value);
  if (!isValid(parsed)) return null;
  return format(parsed, DateFormat.SHORT);
}

function formatWage(result: CandidateVacancyResult): string | null {
  let wage = '';
  if (result.wage_from && result.wage_to) {
    wage = `${toMonetary(result.wage_from)} - ${toMonetary(result.wage_to)}`;
  } else if (result.wage_from) {
    wage = `From ${toMonetary(result.wage_from)}`;
  } else if (result.wage_to) {
    wage = `Up to ${toMonetary(result.wage_to)}`;
  }
  if (!wage) return null;
  if (result.salary_type) {
    wage += ` (${result.salary_type})`;
  }
  return wage;
}

function vacancyMeta(result: CandidateVacancyResult): string {
  const place = [result.region || result.town, result.country]
    .filter(Boolean)
    .join(', ');
  return [
    result.company,
    place,
    // the location filter keeps vacancies with none stated, so say so
    result.work_location || 'Work arrangement not stated',
    result.employment_level,
    result.employment_type,
  ]
    .filter(Boolean)
    .join(' - ');
}

function mustHaveLines(result: CandidateVacancyResult): string[] {
  const lines: string[] = [];
  const mustHaves = result.must_haves || {};
  if (mustHaves.languages && mustHaves.languages.length > 0) {
    lines.push(`Languages: ${mustHaves.languages.join(', ')}`);
  }
  if (mustHaves.min_experience_years) {
    lines.push(`At least ${mustHaves.min_experience_years} years' experience`);
  }
  for (const other of mustHaves.other || []) {
    lines.push(other);
  }
  return lines;
}

interface Props {
  rank: number;
  result: CandidateVacancyResult;
  feedback: Feedback | undefined;
  onFeedbackChange: (next: Feedback | undefined) => void;
  // the ats link was opened
  onOpen?: () => void;
}

export const VacancyResultCard: React.FC<Props> = ({
  rank,
  result,
  feedback,
  onFeedbackChange,
  onOpen,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [expanded, setExpanded] = React.useState(false);

  // only an ai reason goes in the reason slot, same as the candidate cards
  let reason = '';
  if (result.reranked && result.llm_reason) {
    reason = result.llm_reason;
  }
  const wage = formatWage(result);
  const closing = formatDate(result.closing_date);
  const mustHaves = mustHaveLines(result);

  function copyReference() {
    const reference = result.reference ?? '';
    navigator.clipboard
      .writeText(reference)
      .then(() => snackbar.success(`${reference} copied. ${referralSteps}`))
      .catch(() =>
        snackbar.error(`Couldn't copy ${reference}. Please copy it manually.`),
      );
  }

  let matchedAs = 'Someone who does this job now';
  if (result.matched_persona === 'adjacent') {
    matchedAs = 'Someone from a related role';
  }

  return (
    <Paper elevation={0} variant="outlined" className={classes.card}>
      <Box className={classes.badge} aria-label={`Rank ${rank}`}>
        <Typography component="span" className={classes.rank}>
          {rank}
        </Typography>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.topLine}>
          <Typography variant="subtitle1" className={classes.title}>
            {result.title || `Vacancy ${result.vacancy_id}`}
          </Typography>
          <Tooltip title="Open vacancy in ATS">
            <IconButton
              component="a"
              href={`https://konnekt.talexiohr.com/ats/vacancies/${result.vacancy_id}`}
              target="_blank"
              onClick={onOpen}
              // middle click opens a tab too, right click doesn't count
              onAuxClick={(e: React.MouseEvent) => {
                if (e.button === 1) onOpen?.();
              }}
              rel="noopener noreferrer"
              size="small"
              className={classes.linkButton}
              aria-label={`Open vacancy ${result.vacancy_id} in ATS`}
            >
              <OpenInNewIcon className={classes.linkIcon} />
            </IconButton>
          </Tooltip>
          {result.reference && (
            <Box component="span" className={classes.reference}>
              <Tooltip title="Copy the reference, to paste into the candidate's application">
                <IconButton
                  onClick={copyReference}
                  size="small"
                  className={classes.linkButton}
                  aria-label={`Copy reference ${result.reference}`}
                >
                  <FileCopyOutlinedIcon className={classes.linkIcon} />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="textSecondary">
                {result.reference}
              </Typography>
            </Box>
          )}
        </Box>
        {result.leader && (
          <Typography variant="caption" color="textSecondary" display="block">
            Leader: {result.leader}
          </Typography>
        )}
        <Typography variant="body2" className={classes.meta}>
          {vacancyMeta(result)}
        </Typography>
        <Box className={classes.chips}>
          {result.vacancy_status && (
            <Chip
              size="small"
              className={classes.chip}
              label={result.vacancy_status}
            />
          )}
          {wage && <Chip size="small" className={classes.chip} label={wage} />}
          {result.salary_below && (
            <Chip
              size="small"
              variant="outlined"
              className={`${classes.chip} ${classes.payBelowChip}`}
              label="Pays below candidate's salary"
            />
          )}
          {result.is_published === false && (
            <Chip size="small" className={classes.chip} label="Not published" />
          )}
          {result.exclude_non_eu && (
            <Chip size="small" className={classes.chip} label="EU only" />
          )}
          {result.is_internal && (
            <Chip size="small" className={classes.chip} label="Internal" />
          )}
          {closing && (
            <Chip
              size="small"
              className={classes.chip}
              label={`Closes ${closing}`}
            />
          )}
        </Box>
        {reason && (
          <Typography variant="body2" className={classes.matchReason}>
            {reason}
          </Typography>
        )}
      </Box>

      <Box className={classes.scoreCol}>
        <Typography className={classes.scoreValue}>
          {(result.final_score * 100).toFixed(0)}
        </Typography>
        <Typography className={classes.scoreLabel}>Match</Typography>
        <Button
          className={classes.expandBtn}
          size="small"
          onClick={() => setExpanded((x) => !x)}
          endIcon={
            <ExpandMoreIcon
              className={`${classes.expandIcon} ${
                expanded ? classes.expandIconOpen : ''
              }`}
            />
          }
          aria-expanded={expanded}
        >
          Details
        </Button>
      </Box>

      <Box className={classes.wide}>
        <FeedbackControls
          feedback={feedback}
          onChange={onFeedbackChange}
          reasons={badReasons}
        />
      </Box>

      <Box className={classes.wide}>
        <Collapse in={expanded} unmountOnExit>
          <Box className={classes.detailsBox}>
            <Box className={classes.subScores}>
              <Box>
                <Typography className={classes.subScoreLabel}>
                  Similarity
                </Typography>
                <Typography className={classes.subScoreValue}>
                  {(result.vector_score * 100).toFixed(0)}%
                </Typography>
              </Box>
              <Box>
                <Typography className={classes.subScoreLabel}>
                  Overall match
                </Typography>
                <Typography className={classes.subScoreValue}>
                  {(result.final_score * 100).toFixed(0)}%
                </Typography>
              </Box>
              <Box>
                <Typography className={classes.subScoreLabel}>
                  Matched as
                </Typography>
                <Typography className={classes.subScoreValue}>
                  {matchedAs}
                </Typography>
              </Box>
            </Box>
            {mustHaves.length > 0 && (
              <Box>
                <Typography className={classes.subScoreLabel}>
                  Must-haves
                </Typography>
                {mustHaves.map((line) => (
                  <Typography key={line} variant="body2">
                    - {line}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};
